import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { orderApiService } from "../../../../../service/admin/ordersApiService";
import { ProductsApiService } from "../../../../../service/website/productsApiService";
import { initPricingConfig, PRICE_LOGIC } from "../../../../../helper/constants";
import {
  getPriceForQuantity,
  calculateVariantChargeableWeight,
} from "../../../../../website/component/views/productDetails/utils";
import CentralizePriceCalculation from "../../../../../service/pricing/pricingService";
import {
  ToastTypes,
  useMessage,
} from "../../../../../base/context/MessageProvider";
import { normalizeUrl, appendSizeToImageUrl } from "../../../../../utils/imageUtils";
import { getOrderItemPricingMode } from "../../../../../utils/orderPricingMode";
import "./style.css";

const ORDER_STATUSES = [
  "abnormal", "payment pending", "payment paid", "processing",
  "order processed to supplier", "partially-shipped", "shipped",
  "delivered", "cancel", "refunded",
];
const PAYMENT_STATUSES = ["", "paid", "pending", "refunded", "partially_refunded", "voided"];

const PLACEHOLDER = "/placeholder-image.svg";
const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
const RMB_FMT = new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 4 });

/** Zoom preview only (kept here so modal does not rely on shared utils behavior). */
function editOrderZoomIsAwsPassthrough(url) {
  if (!url || typeof url !== "string") return false;
  const u = url.toLowerCase();
  return (
    u.includes("amazonaws.com") ||
    u.includes("cloudfront.net") ||
    u.includes("importerr-pimages") ||
    /s3\.[a-z0-9-]+\.amazonaws\.com/.test(u)
  );
}

/** Alibaba-style paths: strip .WxH. then request .800x800.; AWS URLs unchanged. */
function editOrderZoomPrimarySrc(url) {
  if (!url || typeof url !== "string") return url;
  if (editOrderZoomIsAwsPassthrough(url)) return url;
  const parts = url.split("?");
  const path = parts[0];
  const qs = parts.length > 1 ? `?${parts.slice(1).join("?")}` : "";
  if (/\.800x800\.(jpg|jpeg|png|webp)$/i.test(path)) return url;
  const withoutOld = path.replace(/\.(\d+x\d+)\.(jpg|jpeg|png|webp)$/i, ".$2");
  const sized = withoutOld.replace(/\.(jpg|jpeg|png|webp)$/i, (_, ext) => `.800x800.${ext}`);
  if (sized === withoutOld) return url;
  return sized + qs;
}

function parseOptionalNum(val) {
  if (val == null || val === "") return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

/** Alibaba list / consign prices in RMB for display under unit price */
function variantApCpDisplay(src) {
  if (!src || typeof src !== "object") return { ap: null, cp: null };
  return {
    ap: parseOptionalNum(src.ap),
    cp: parseOptionalNum(src.cp ?? src.consignPrice),
  };
}

function resolveVariantRmbSource(lineItem, product) {
  if (lineItem?.rmbSource) return lineItem.rmbSource;
  if (product?.variations?.length) {
    const numSku = Number(lineItem.skuId);
    const matched = product.variations.find(
      (x) => x.skuId?.toString() === String(lineItem.skuId) || x.skuId === numSku
    );
    if (matched) return matched;
  }
  const fb = lineItem?.rmbFallback;
  if (fb && (fb.ap != null || fb.cp != null || fb.consignPrice != null)) return fb;
  return null;
}

/** Weight (kg) and L×W×H (cm) for display — same source as pricing dims */
function formatVariantWtDims(v) {
  const src = v.rmbSource || {};
  const wt = Number(src.weight ?? v.variantWeight);
  const len = Number(src.length ?? v.variantLength);
  const wid = Number(src.width ?? v.variantWidth);
  const hgt = Number(src.height ?? v.variantHeight);
  const wtLabel = Number.isFinite(wt) && wt > 0 ? `${wt} kg` : "—";
  const dimLabel =
    Number.isFinite(len) && len > 0 && Number.isFinite(wid) && wid > 0 && Number.isFinite(hgt) && hgt > 0
      ? `${len}×${wid}×${hgt} cm`
      : "—";
  return { wtLabel, dimLabel };
}

/** Wholesale / cart: CP-first RMB base (same as CartProductCard + PDP) */
function wholesaleRmbBase(src) {
  if (!src || typeof src !== "object") return 0;
  return (
    parseFloat(
      src.cp ||
        src.consignPrice ||
        src.ap ||
        src.fenxiaoPriceInfo?.onePiecePrice ||
        src.fenxiaoPriceInfo?.offerPrice ||
        src.price ||
        0
    ) || 0
  );
}

/** Dropship: AP-first RMB base (user / PDP one-piece sourcing) */
function dropshipRmbBase(src) {
  if (!src || typeof src !== "object") return 0;
  return (
    parseFloat(
      // Match PDP logic (calculateDropshippingPrice): ap -> cp -> onePiece -> consignPrice -> offerPrice -> price
      src.ap ||
        src.cp ||
        src.fenxiaoPriceInfo?.onePiecePrice ||
        src.consignPrice ||
        src.fenxiaoPriceInfo?.offerPrice ||
        src.price ||
        0
    ) || 0
  );
}

function detectIsMinMaxRange(product) {
  const vars = product?.variations;
  if (!Array.isArray(vars) || vars.length < 2) return false;
  const prices = vars
    .map((v) => parseFloat(v.wholesaleFinalPrice || v.finalPrice || 0))
    .filter((n) => n > 0);
  if (prices.length < 2) return false;
  return Math.min(...prices) !== Math.max(...prices);
}

/** Same dw/vw aggregation as PDP / CartProductCard (chargeable weight = max rules, not raw API chargeableWeight). */
function variantLineDwVwForPricing(qty, v) {
  const src = v.rmbSource || {};
  const dims = {
    weight: Number(src.weight ?? v.variantWeight) || 0,
    length: Number(src.length ?? v.variantLength) || 0,
    width: Number(src.width ?? v.variantWidth) || 0,
    height: Number(src.height ?? v.variantHeight) || 0,
  };
  const { chargeableWeight } = calculateVariantChargeableWeight(dims);
  const h = dims.height;
  const wi = dims.width;
  const l = dims.length;
  const lineVol = h && wi && l ? h * wi * l * qty : 0;
  return { lineWeight: chargeableWeight * qty, lineVol, unitCount: qty };
}

/**
 * Merge order-level wholesale breakdown rows from the latest pricing-algorithm result
 * (per product group) into the existing order.breakdown snapshot so admin order details
 * and Importerr Price (FBP) stay in sync after save.
 */
function mergeWholesaleBreakdownFromPricing(orderData, grouped, pricingMap) {
  const prev = Array.isArray(orderData?.breakdown) ? orderData.breakdown.map((b) => (b && typeof b === "object" ? { ...b } : b)) : [];
  const indexByPid = new Map();
  prev.forEach((b, i) => {
    if (b != null && b.productId != null) indexByPid.set(String(b.productId), i);
  });
  for (const g of grouped) {
    const mapKey = g.productId || g.productSku;
    const p = pricingMap[mapKey];
    if (!p?.breakdown) continue;
    const pid = g.productId != null ? String(g.productId) : "";
    if (!pid) continue;
    const inner = p.breakdown;
    const breakdownArr = Array.isArray(inner) ? inner : [inner];
    const entry = {
      productId: pid,
      pricingMode: "wholesale",
      breakdown: breakdownArr,
    };
    const idx = indexByPid.get(pid);
    if (idx !== undefined) prev[idx] = entry;
    else {
      indexByPid.set(pid, prev.length);
      prev.push(entry);
    }
  }
  return prev;
}

async function shippingFromCheckoutLMD(items) {
  const byKey = new Map();
  for (const i of items) {
    const productKey = i.productId || i.productSku || i.productName;
    // Keep WS and DS separated even for same product so LMD applies per mode correctly.
    const modeKey = `${productKey}__${i.pricingMode || "dropshipping"}`;
    if (!byKey.has(modeKey)) {
      byKey.set(modeKey, { pricingMode: i.pricingMode, variants: [] });
    }
    byKey.get(modeKey).variants.push({
      skuId: i.skuId,
      weight: i.variantWeight,
      height: i.variantHeight,
      width: i.variantWidth,
      length: i.variantLength,
      quantity: i.quantity,
    });
  }
  const products = Array.from(byKey.values());
  const LMD_Details = await CentralizePriceCalculation.calculateCartProductTotalLMD(products);
  return {
    totalLMD: LMD_Details.totalLMD,
    gstOnLMD: LMD_Details.gstOnLMD,
    totalLMDWithGST: LMD_Details.totalLMDWithGST,
    gstFreightPercent:
      LMD_Details.totalLMD > 0 ? LMD_Details.gstOnLMD / LMD_Details.totalLMD : 0.18,
  };
}

function optimizeImg(url) {
  if (!url || url === PLACEHOLDER) return PLACEHOLDER;
  return appendSizeToImageUrl(normalizeUrl(url), "100x100");
}
function getVariantImage(d) {
  const attrs = d?.variant?.skuAttributes || [];
  const attrImg = attrs.find((a) => a?.skuImageUrl)?.skuImageUrl;
  return optimizeImg(attrImg || d?.variant?.skuImageUrl || d?.variant?.image || d?.image || d?.product?.imgUrl?.[0]);
}
function getProductImage(d) {
  return optimizeImg(d?.product?.imgUrl?.[0] || d?.image);
}
function getVariantLabel(d) {
  const attrs = d?.variant?.skuAttributes || d?.skuAttributes || [];
  if (attrs.length > 0)
    return attrs.map((a) => `${a?.attributeNameTrans || a?.attributeName || ""}: ${a?.valueTrans || a?.value || ""}`).join(", ");
  return d?.sku || d?.variant?.skuId || "Default";
}
function getPricingMode(detail, orderBreakdown, orderType) {
  const isManualOrder = Boolean(orderType);
  return getOrderItemPricingMode(detail, {
    orderBreakdown,
    orderType,
    isManualOrder,
    dropshippingLabel: "dropshipping",
  });
}

export default function EditOrderModal({ data, onClose, onSaved }) {
  const message = useMessage();
  const [saving, setSaving] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [pricingReady, setPricingReady] = useState(false);
  const [calculatingAll, setCalculatingAll] = useState(false);
  // When qty changes, prices/shipping are stale until user clicks "Calculate"
  const [pricingDirty, setPricingDirty] = useState(false);
  const [qtyDrafts, setQtyDrafts] = useState({});
  const [productMap, setProductMap] = useState({});
  const [pricingMap, setPricingMap] = useState({});
  const [shippingCalc, setShippingCalc] = useState({
    totalLMD: 0,
    gstOnLMD: 0,
    totalLMDWithGST: 0,
    gstFreightPercent: 0.18,
  });
  const catalogPricingDoneRef = useRef(false);
  const [zoomSrc, setZoomSrc] = useState(null);
  const zoomUrlsRef = useRef([]);

  const isManualOrder =
    data?.marketplace?.toLowerCase() === "manual" ||
    data?.createdVia === "manual_admin" ||
    data?.createdVia === "split_admin";
  const orderType = isManualOrder ? data?.orderType : null;

  const [recipient, setRecipient] = useState({
    recipientName: data?.recipientName || "",
    shippingPhoneNumber: data?.shippingPhoneNumber || "",
    shippingAddress1: data?.shippingAddress1 || "",
    shippingAddress2: data?.shippingAddress2 || "",
    shippingAddress3: data?.shippingAddress3 || "",
    shippingCity: data?.shippingCity || "",
    shippingState: data?.shippingState || "",
    shippingPostalCode: data?.shippingPostalCode || "",
  });
  const [status, setStatus] = useState(data?.status || "");
  const [paymentStatus, setPaymentStatus] = useState(data?.mkp_financial_status || "");

  const [items, setItems] = useState(() =>
    (data?.orderDetails || []).map((d) => {
      const mode = getPricingMode(d, data?.breakdown, orderType);
      const v = d?.variant;
      let unitPrice = 0;
      if (mode === "wholesale")
        unitPrice =
          Number(v?.wholesaleFinalPrice) || Number(v?.finalPrice) || Number(v?.breakdown?.FBP ?? v?.breakdown?.fbp) || 0;
      else unitPrice = Number(v?.finalPrice) || Number(v?.breakdown?.FBP ?? v?.breakdown?.fbp) || 0;
      return {
        _id: d._id,
        quantity: Number(d.quantity) || 1,
        minOrderQty: Number(d?.product?.moq) || 1,
        unitPrice,
        dbUnitPrice: unitPrice,
        label: getVariantLabel(d),
        sku: d?.sku || d?.variant?.skuId || d?.variant?.sku || "-",
        skuId: d?.variant?.skuId || d?.sku,
        variantImage: getVariantImage(d),
        productImage: getProductImage(d),
        productName: d?.product?.title || d?.productName || d?.name || "Product",
        productSku: d?.product?.sku || d?.sku || "",
        productId: d?.product?._id || d?.productId || "",
        offerId: d?.product?.offerId || d?.product?.sku || d?.sku || "",
        pricingMode: mode,
        variantWeight: Number(v?.weight || v?.chargeableWeight) || 0,
        variantHeight: Number(v?.height) || 0,
        variantWidth: Number(v?.width) || 0,
        variantLength: Number(v?.length) || 0,
        rmbSource: null,
        rmbFallback:
          v && typeof v === "object"
            ? { ap: v.ap, cp: v.cp, consignPrice: v.consignPrice }
            : null,
      };
    })
  );

  useEffect(() => {
    initPricingConfig().then(() => setPricingReady(true));
  }, []);

  useEffect(() => {
    const ids = [...new Set(items.map((i) => i.productId).filter(Boolean))];
    if (!ids.length) {
      setLoadingProducts(false);
      return;
    }
    let cancelled = false;
    orderApiService
      .fetchProductsForEditOrder(ids)
      .then((r) => {
        if (cancelled) return;
        const list = r?.status?.code === 200 && Array.isArray(r.data?.products) ? r.data.products : [];
        const map = {};
        list.forEach((p) => {
          if (p?._id != null) map[String(p._id)] = p;
        });
        setProductMap(map);
        setItems((prev) =>
          prev.map((item) => {
            const product = map[String(item.productId)];
            if (!product?.variations?.length) return item;
            const numSku = Number(item.skuId);
            const matched = product.variations.find(
              (v) => v.skuId?.toString() === String(item.skuId) || v.skuId === numSku
            );
            if (!matched) return item;
            let price =
              item.pricingMode === "wholesale"
                ? Number(matched.wholesaleFinalPrice) || Number(matched.finalPrice) || 0
                : Number(matched.finalPrice) || 0;
            const w = Number(matched.weight) || item.variantWeight;
            const h = Number(matched.height) || item.variantHeight;
            const wi = Number(matched.width) || item.variantWidth;
            const l = Number(matched.length) || item.variantLength;
            return {
              ...item,
              unitPrice: price > 0 ? price : item.unitPrice,
              dbUnitPrice: price > 0 ? price : item.dbUnitPrice,
              minOrderQty: Number(product?.moq) || item.minOrderQty || 1,
              variantWeight: w,
              variantHeight: h,
              variantWidth: wi,
              variantLength: l,
              rmbSource: matched,
              rmbFallback: null,
            };
          })
        );
      })
      .catch(() => {
        if (!cancelled) setProductMap({});
      })
      .finally(() => {
        if (!cancelled) setLoadingProducts(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((item, idx) => {
      const key = item.productId || item.productSku || item.productName;
      if (!map.has(key)) {
        map.set(key, {
          name: item.productName,
          productSku: item.productSku,
          moq: Number(item.minOrderQty) || 1,
          productImage: item.productImage,
          offerId: item.offerId,
          productId: item.productId,
          variants: [],
        });
      }
      map.get(key).variants.push({ ...item, idx });
    });
    return Array.from(map.values());
  }, [items]);

  useEffect(() => {
    // Shipping is recalculated only inside "Calculate" (recalcAllPricingAndShipping),
    // not on every qty change (otherwise it causes slow API calls).
  }, []);

  const fetchPricing = useCallback(
    async (groupKey, groupVariants) => {
      setPricingMap((prev) => ({
        ...prev,
        [groupKey]: { calculating: true, fbp: prev[groupKey]?.fbp || null, breakdown: prev[groupKey]?.breakdown || null },
      }));

      const defaultDw = PRICE_LOGIC.DEFAULT_DW ?? 0.5;
      const volumeFactor = PRICE_LOGIC.VOLUME_FACTOR ?? 5000;
      const hsnTaxPercent = PRICE_LOGIC.HSNTaxPercent ?? 18;

      try {
        const wholesaleVariants = groupVariants.filter((v) => v.pricingMode === "wholesale");
        const logisticsSkuNumModels = wholesaleVariants
          .filter((v) => v.quantity > 0 && v.skuId)
          .map((v) => ({ skuId: v.skuId, quantity: v.quantity }));

        const effectiveTotalQty = wholesaleVariants.reduce((s, v) => s + (v.quantity || 0), 0);
        if (effectiveTotalQty === 0) {
          setPricingMap((prev) => ({ ...prev, [groupKey]: { calculating: false, fbp: null, breakdown: null } }));
          return;
        }

        const product = productMap[wholesaleVariants[0]?.productId] || {};
        const offerId = product?.offerId || product?.offer_id || product?.sku || wholesaleVariants[0]?.offerId;
        const categoryId = product?.categoryId || "";

        let shippingCost = 0;
        // Wholesale uses real freight cost, same as cart/PDP wholesale flow.
        if (offerId && logisticsSkuNumModels.length) {
          try {
            const freightResult = await ProductsApiService.getLocalShippingCost(parseFloat(offerId), logisticsSkuNumModels);
            if (freightResult?.success) shippingCost = Number(freightResult.shippingCost) || 0;
          } catch {
            shippingCost = 0;
          }
        }

        const priceRanges =
          product?.priceRanges?.length > 0 ? product.priceRanges : [];
        const isMinMaxRange = detectIsMinMaxRange(product);

        let totalVariantsBasePrice = 0;
        let totalVariantsWeight = 0;
        let totalVariantsVolume = 0;
        let totalVariants = 0;

        wholesaleVariants.forEach((v) => {
          const q = v.quantity || 0;
          if (q <= 0) return;
          const src = v.rmbSource || {};
          const { lineWeight, lineVol, unitCount } = variantLineDwVwForPricing(q, v);
          totalVariantsWeight += lineWeight;
          totalVariantsVolume += lineVol;
          totalVariants += unitCount;

          const originalPrice = wholesaleRmbBase(src);
          let priceToUse = originalPrice;
          if (priceRanges.length > 0 && effectiveTotalQty > 0 && !isMinMaxRange) {
            const rangePriceObj = getPriceForQuantity(originalPrice, effectiveTotalQty, priceRanges);
            if (rangePriceObj != null) {
              priceToUse = rangePriceObj.price != null ? parseFloat(rangePriceObj.price) : originalPrice;
            } else if (priceRanges[0]?.price) {
              priceToUse = parseFloat(priceRanges[0].price);
            }
          }
          totalVariantsBasePrice += priceToUse * q;
        });

        let shouldUseReverseCalculation = false;
        let totalDbFinalPrice = 0;

        if (offerId) {
          try {
            const priceLogsRes = await ProductsApiService.checkPriceLogsForOffer(offerId);
            if (priceLogsRes?.status?.code === 200 && priceLogsRes?.data?.hasPriceLogs && priceLogsRes?.data?.variantPrices && effectiveTotalQty === 1) {
              const dbVariantPrices = priceLogsRes.data.variantPrices;
              wholesaleVariants.forEach((v) => {
                const q = v.quantity || 0;
                if (q > 0 && dbVariantPrices[v.skuId]) {
                  const dbVariant = dbVariantPrices[v.skuId];
                  const dbPrice = parseFloat(dbVariant.wholesaleFinalPrice || dbVariant.finalPrice || 0);
                  totalDbFinalPrice += dbPrice * q;
                }
              });
              shouldUseReverseCalculation = totalDbFinalPrice > 0;
            }
          } catch {
            /* ignore */
          }
        }

        const dw =
          totalVariantsWeight > 0 ? totalVariantsWeight : defaultDw * (totalVariants || effectiveTotalQty);
        const vw = totalVariantsVolume > 0 ? totalVariantsVolume / volumeFactor : null;

        let calculationData = {
          ap: totalVariantsBasePrice,
          ls: shippingCost,
          hsn: null,
          hsnTaxPercent,
          dw,
          vw,
          categoryId,
          topCategoryId: product?.topCategoryId || "",
          secondCategoryId: product?.secondCategoryId || "",
          thirdCategoryId: product?.thirdCategoryId || "",
          productShippingInfo: product?.productShippingInfo || {},
        };

        if (shouldUseReverseCalculation && totalDbFinalPrice > 0 && effectiveTotalQty === 1) {
          calculationData = {
            ...calculationData,
            dbFinalPrice: totalDbFinalPrice,
            pricingMode: "wholesale",
            offerId: String(offerId),
            totalQuantity: effectiveTotalQty,
            logisticsSkuNumModels,
          };
        }

        if (totalVariantsBasePrice <= 0 && !shouldUseReverseCalculation) {
          const inrFallback = wholesaleVariants.reduce((s, v) => s + (Number(v.dbUnitPrice) || 0) * (v.quantity || 0), 0);
          if (inrFallback > 0) {
            calculationData = {
              ap: 0,
              ls: shippingCost,
              hsn: null,
              hsnTaxPercent,
              dw,
              vw,
              categoryId,
              topCategoryId: product?.topCategoryId || "",
              secondCategoryId: product?.secondCategoryId || "",
              thirdCategoryId: product?.thirdCategoryId || "",
              productShippingInfo: product?.productShippingInfo || {},
              dbFinalPrice: inrFallback,
              pricingMode: "wholesale",
              offerId: String(offerId || ""),
              totalQuantity: effectiveTotalQty,
              logisticsSkuNumModels,
            };
          } else {
            setPricingMap((prev) => ({ ...prev, [groupKey]: { calculating: false, fbp: 0, breakdown: null } }));
            return;
          }
        }

        const res = await ProductsApiService.calculatePricingAlgorithm(calculationData);
        if (res?.status?.code === 200 && res?.data?.breakdown) {
          const bd = res.data.breakdown;
          setPricingMap((prev) => ({
            ...prev,
            [groupKey]: { calculating: false, fbp: Number(bd.FBP) || 0, breakdown: bd },
          }));
        } else {
          setPricingMap((prev) => ({ ...prev, [groupKey]: { calculating: false, fbp: null, breakdown: null } }));
        }
      } catch {
        setPricingMap((prev) => ({ ...prev, [groupKey]: { calculating: false, fbp: null, breakdown: null } }));
      }
    },
    [productMap]
  );

  const recalcAllPricingAndShipping = useCallback(async () => {
    await Promise.all(grouped.map((g) => fetchPricing(g.productId || g.productSku, g.variants)));
    const ship = await shippingFromCheckoutLMD(items);
    setShippingCalc((prev) => ({ ...ship, gstFreightPercent: ship.gstFreightPercent ?? prev.gstFreightPercent }));
  }, [grouped, fetchPricing, items]);

  useEffect(() => {
    if (loadingProducts || !pricingReady || catalogPricingDoneRef.current) return;
    catalogPricingDoneRef.current = true;
    setPricingDirty(true);
    setCalculatingAll(true);
    recalcAllPricingAndShipping()
      .then(() => setPricingDirty(false))
      .catch(() => setPricingDirty(true))
      .finally(() => setCalculatingAll(false));
  }, [loadingProducts, pricingReady, recalcAllPricingAndShipping]);

  const totals = useMemo(() => {
    let productTotal = 0;
    let totalGST = 0;
    grouped.forEach((g) => {
      const key = g.productId || g.productSku;
      const p = pricingMap[key];
      const dsTotal = g.variants
        .filter((v) => v.pricingMode !== "wholesale")
        .reduce((s, v) => s + v.unitPrice * v.quantity, 0);
      if (p?.fbp > 0) {
        productTotal += p.fbp + dsTotal;
        if (p.breakdown)
          totalGST +=
            (Number(p.breakdown.GST_PR) || 0) + (Number(p.breakdown.GST_FR) || 0) + (Number(p.breakdown.GST_PF) || 0);
      } else {
        g.variants.forEach((v) => {
          productTotal += v.unitPrice * v.quantity;
        });
      }
    });
    const shipping = shippingCalc.totalLMDWithGST;
    const gstOnShipping = shippingCalc.gstOnLMD;
    const imDiscount = Number(data?.imPointsDiscount) || 0;
    const pct = Math.round((shippingCalc.gstFreightPercent || 0) * 100);
    return {
      productTotal,
      totalGST,
      shipping,
      gstOnShipping,
      baseLMD: shippingCalc.totalLMD,
      imDiscount,
      grand: productTotal + shipping - imDiscount,
      gstShippingPctLabel: pct > 0 && pct <= 100 ? pct : 18,
    };
  }, [grouped, pricingMap, shippingCalc, data]);

  const handleRecipientChange = useCallback((f, v) => {
    setRecipient((p) => ({ ...p, [f]: v }));
  }, []);

  const commitQtyValue = useCallback((idx, raw) => {
    const parsed = parseInt(String(raw).trim(), 10);
    const qty = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    setPricingDirty(true);
    setItems((prev) => {
      const c = [...prev];
      c[idx] = { ...c[idx], quantity: qty };
      return c;
    });
    setQtyDrafts((prev) => {
      const n = { ...prev };
      delete n[idx];
      return n;
    });
  }, []);

  const handleQtyChange = useCallback((idx, value) => {
    if (value === "" || /^\d+$/.test(value)) setQtyDrafts((prev) => ({ ...prev, [idx]: value }));
  }, []);

  const adjustQty = useCallback((idx, delta) => {
    setQtyDrafts((prev) => {
      const n = { ...prev };
      delete n[idx];
      return n;
    });
    setPricingDirty(true);
    setItems((prev) => {
      const c = [...prev];
      const cur = Number(c[idx]?.quantity) || 1;
      c[idx] = { ...c[idx], quantity: Math.max(1, cur + delta) };
      return c;
    });
  }, []);

  const handleCalculateAll = useCallback(async () => {
    if (loadingProducts || !pricingReady) return;
    setCalculatingAll(true);
    setPricingDirty(true);
    try {
      await recalcAllPricingAndShipping();
      setPricingDirty(false);
      message.showToast("Prices & shipping recalculated", ToastTypes.SUCCESS);
    } catch (err) {
      setPricingDirty(true);
      message.showToast(err?.message || "Recalculation failed", ToastTypes.ERROR);
    } finally {
      setCalculatingAll(false);
    }
  }, [loadingProducts, pricingReady, recalcAllPricingAndShipping, message]);

  const handleSave = async () => {
    const wsByProduct = new Map();
    items.forEach((i) => {
      if (i.pricingMode !== "wholesale") return;
      const key = String(i.productId || i.productSku || i.productName || "");
      if (!wsByProduct.has(key)) {
        wsByProduct.set(key, {
          wsQty: 0,
          moq: Number(i.minOrderQty) || 1,
          productSku: i.productSku || "-",
        });
      }
      const rec = wsByProduct.get(key);
      rec.wsQty += Number(i.quantity) || 0;
      if (!(rec.moq > 0)) rec.moq = Number(i.minOrderQty) || 1;
      if (!rec.productSku || rec.productSku === "-") rec.productSku = i.productSku || "-";
    });
    const invalidProduct = Array.from(wsByProduct.values()).find((p) => p.wsQty < p.moq);
    if (invalidProduct) {
      message.showToast(
        `Minimum qty required for WS product is ${invalidProduct.moq}. Product SKU: ${invalidProduct.productSku}.`,
        ToastTypes.WARNING
      );
      return;
    }
    if (pricingDirty) {
      message.showToast("Click Calculate first to update prices & shipping.", ToastTypes.ERROR);
      return;
    }
    setSaving(true);
    try {
      const lineConsumerPriceByIdx = {};
      grouped.forEach((group) => {
        const gk = group.productId || group.productSku;
        const pricing = pricingMap[gk];
        const wsQty = group.variants
          .filter((v) => v.pricingMode === "wholesale")
          .reduce((s, v) => s + (v.quantity || 0), 0);
        group.variants.forEach((v) => {
          const linePrice =
            v.pricingMode === "wholesale" && pricing?.fbp > 0 && wsQty > 0
              ? (pricing.fbp / wsQty) * v.quantity
              : v.unitPrice * v.quantity;
          lineConsumerPriceByIdx[v.idx] = Math.round((Number(linePrice) || 0) * 100) / 100;
        });
      });
      const productTotal = Math.round(totals.productTotal * 100) / 100;
      const breakdown = mergeWholesaleBreakdownFromPricing(data, grouped, pricingMap);
      const payload = {
        recipient,
        status,
        mkp_financial_status: paymentStatus,
        orderDetails: items.map((i, idx) => ({
          _id: i._id,
          orderItemId: i.orderItemId,
          quantity: i.quantity,
          consumerPrice: lineConsumerPriceByIdx[idx],
        })),
        totalShippingCharges: Math.round(shippingCalc.totalLMDWithGST * 100) / 100,
        gstOnShipping: Math.round(shippingCalc.gstOnLMD * 100) / 100,
        breakdown,
        mkp_current_total_price: productTotal,
        mkp_current_subtotal_price: productTotal,
      };
      const res = await orderApiService.adminEditOrder(data._id, payload);
      if (res?.status?.code === 200) {
        message.showToast("Order updated successfully", ToastTypes.SUCCESS);
        if (onSaved) onSaved();
        onClose();
      } else message.showToast(res?.error?.message || "Failed to update order", ToastTypes.ERROR);
    } catch (err) {
      message.showToast(err?.message || "Server error", ToastTypes.ERROR);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const h = (e) => {
      if (e.key !== "Escape") return;
      if (zoomSrc) {
        zoomUrlsRef.current = [];
        setZoomSrc(null);
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose, zoomSrc]);

  const openImageZoom = useCallback((url) => {
    if (!url || url === PLACEHOLDER) return;
    const raw = String(url).trim();
    let base = normalizeUrl(raw);
    if (!base && raw.startsWith("/") && typeof window !== "undefined" && window.location?.origin) {
      base = `${window.location.origin}${raw}`;
    }
    if (!base) base = raw;
    const primary = editOrderZoomPrimarySrc(base);
    const candidates = [];
    if (primary && primary !== base) candidates.push(primary);
    if (base) candidates.push(base);
    if (raw && raw !== base) candidates.push(raw);
    const unique = [...new Set(candidates.filter(Boolean))];
    zoomUrlsRef.current = unique;
    setZoomSrc(unique[0] || null);
  }, []);

  const gstShipLabel = totals.gstShippingPctLabel;

  return (
    <div className="edit-order-overlay" onClick={onClose}>
      <div className="edit-order-modal" onClick={(e) => e.stopPropagation()}>
        <div className="edit-order-header">
          <h3>
            <i className="fas fa-edit" style={{ marginRight: 6, color: "#3b82f6" }} /> Edit Order — {data?.upsaleoOrderId}
          </h3>
          <button className="edit-order-close" onClick={onClose} type="button">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="edit-order-body">
          <div className="edit-order-section edit-order-section-recipient">
            <div className="edit-order-section-title">Recipient Information</div>
            <div className="edit-order-grid-recipient">
              <div className="edit-order-field">
                <label>Name</label>
                <input value={recipient.recipientName} onChange={(e) => handleRecipientChange("recipientName", e.target.value)} />
              </div>
              <div className="edit-order-field">
                <label>Phone</label>
                <input value={recipient.shippingPhoneNumber} onChange={(e) => handleRecipientChange("shippingPhoneNumber", e.target.value)} />
              </div>
              <div className="edit-order-field">
                <label>Postal</label>
                <input value={recipient.shippingPostalCode} onChange={(e) => handleRecipientChange("shippingPostalCode", e.target.value)} />
              </div>
              <div className="edit-order-field">
                <label>City</label>
                <input value={recipient.shippingCity} onChange={(e) => handleRecipientChange("shippingCity", e.target.value)} />
              </div>
              <div className="edit-order-field">
                <label>State</label>
                <input value={recipient.shippingState} onChange={(e) => handleRecipientChange("shippingState", e.target.value)} />
              </div>
            </div>
            <div className="edit-order-grid-recipient-row2">
              <div className="edit-order-field">
                <label>Address line 1</label>
                <input value={recipient.shippingAddress1} onChange={(e) => handleRecipientChange("shippingAddress1", e.target.value)} />
              </div>
              <div className="edit-order-field">
                <label>Address line 2</label>
                <input value={recipient.shippingAddress2} onChange={(e) => handleRecipientChange("shippingAddress2", e.target.value)} />
              </div>
              <div className="edit-order-field">
                <label>Order status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  {ORDER_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="edit-order-field">
                <label>Payment status</label>
                <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                  {PAYMENT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "—"}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="edit-order-section">
            <div className="edit-order-section-title">Order Items</div>
            {loadingProducts ? (
              <div className="edit-order-loading">Loading product data...</div>
            ) : (
              grouped.map((group, gi) => {
                const gk = group.productId || group.productSku;
                const pricing = pricingMap[gk];
                const gqty = group.variants.reduce((s, v) => s + v.quantity, 0);
                const wsQty = group.variants
                  .filter((v) => v.pricingMode === "wholesale")
                  .reduce((s, v) => s + (v.quantity || 0), 0);
                const dsSubtotal = group.variants
                  .filter((v) => v.pricingMode !== "wholesale")
                  .reduce((s, v) => s + v.unitPrice * v.quantity, 0);
                return (
                  <div className="edit-order-product" key={gi}>
                    <div className="edit-order-product-header">
                      <button
                        type="button"
                        className="edit-order-thumb-btn"
                        title="View larger"
                        onClick={() => openImageZoom(group.productImage)}
                      >
                        <img
                          src={group.productImage || PLACEHOLDER}
                          alt=""
                          className="edit-order-product-img"
                          onError={(e) => {
                            if (e.target.src !== PLACEHOLDER) e.target.src = PLACEHOLDER;
                          }}
                        />
                      </button>
                      <div className="edit-order-product-meta">
                        <div className="edit-order-product-name" title={group.name}>
                          {group.name}
                        </div>
                        <div className="edit-order-product-sku">SKU: {group.productSku || "-"} MOQ: {group.moq || "-"}</div>
                      </div>
                    </div>
                    <div className="edit-order-vtable-head">
                      <span className="eov-img">Img</span>
                      <span className="eov-attr">Variant</span>
                      <span className="eov-wtdim eov-wtdim-head">
                        <span>Wt</span>
                        <span className="eov-wtdim-sub">L×W×H cm</span>
                      </span>
                      <span className="eov-type">Type</span>
                      <span className="eov-price eov-price-head-col">
                        <span className="eov-price-head-main">Unit</span>
                        <span className="eov-price-head-sub">AP / CP ¥</span>
                      </span>
                      <span className="eov-qty">Qty</span>
                      <span className="eov-total">Order</span>
                    </div>
                    {group.variants.map((v) => {
                      const op =
                        v.pricingMode === "wholesale" && pricing?.fbp > 0 && wsQty > 0
                          ? (pricing.fbp / wsQty) * v.quantity
                          : v.unitPrice * v.quantity;
                      const rmbSrc = resolveVariantRmbSource(v, productMap[group.productId]);
                      const { ap: apDisp, cp: cpDisp } = variantApCpDisplay(rmbSrc);
                      const { wtLabel, dimLabel } = formatVariantWtDims(v);
                      const qtyShown = Object.prototype.hasOwnProperty.call(qtyDrafts, v.idx) ? qtyDrafts[v.idx] : String(v.quantity);
                      const qtyParsed = parseInt(String(qtyShown).trim(), 10);
                      const qtyFloor = Number.isFinite(qtyParsed) && qtyParsed >= 1 ? qtyParsed : 1;
                      return (
                        <div className="edit-order-variant-row" key={v._id}>
                          <div className="eov-img">
                            <button
                              type="button"
                              className="edit-order-thumb-btn edit-order-thumb-btn-sm"
                              title="View larger"
                              onClick={() => openImageZoom(v.variantImage)}
                            >
                              <img
                                src={v.variantImage || PLACEHOLDER}
                                alt=""
                                className="edit-order-variant-img"
                                onError={(e) => {
                                  if (e.target.src !== PLACEHOLDER) e.target.src = PLACEHOLDER;
                                }}
                              />
                            </button>
                          </div>
                          <div className="eov-attr">
                            <div className="eov-attr-text">{v.label}</div>
                            <div className="eov-sku-text">SKU: {v.sku}</div>
                          </div>
                          <div className="eov-wtdim" title="Weight (kg) and dimensions (cm)">
                            <div className="eov-wtdim-line">{wtLabel}</div>
                            <div className="eov-wtdim-line eov-wtdim-dim">{dimLabel}</div>
                          </div>
                          <div className="eov-type">
                            <span className={`eov-type-badge ${v.pricingMode === "wholesale" ? "eov-ws" : "eov-ds"}`}>
                              {v.pricingMode === "wholesale" ? "WS" : "DS"}
                            </span>
                          </div>
                          <div className="eov-price">
                            <div className="eov-price-inr">{INR.format(v.unitPrice)}</div>
                            {(apDisp != null || cpDisp != null) && (
                              <div className="eov-apcp" title="Variant list (AP) and consign (CP) price in RMB">
                                {apDisp != null && (
                                  <span className="eov-apcp-line">
                                    AP <span className="eov-apcp-num">¥{RMB_FMT.format(apDisp)}</span>
                                  </span>
                                )}
                                {cpDisp != null && (
                                  <span className="eov-apcp-line">
                                    CP <span className="eov-apcp-num">¥{RMB_FMT.format(cpDisp)}</span>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="eov-qty">
                            <div className="eov-qty-stepper">
                              <button
                                type="button"
                                className="eov-qty-btn"
                                aria-label="Decrease quantity"
                                disabled={saving || calculatingAll || qtyFloor <= 1}
                                onClick={() => adjustQty(v.idx, -1)}
                              >
                                −
                              </button>
                              <input
                                type="text"
                                inputMode="numeric"
                                className="eov-qty-input"
                                disabled={saving || calculatingAll}
                                value={Object.prototype.hasOwnProperty.call(qtyDrafts, v.idx) ? qtyDrafts[v.idx] : v.quantity}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => handleQtyChange(v.idx, e.target.value)}
                                onBlur={(e) => commitQtyValue(v.idx, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") e.currentTarget.blur();
                                }}
                              />
                              <button
                                type="button"
                                className="eov-qty-btn"
                                aria-label="Increase quantity"
                                disabled={saving || calculatingAll}
                                onClick={() => adjustQty(v.idx, 1)}
                              >
                                +
                              </button>
                            </div>
                          </div>
                          <div className="eov-total">
                            {pricing?.calculating ? <span className="eov-calc">calc...</span> : INR.format(op)}
                          </div>
                        </div>
                      );
                    })}
                    <div className="edit-order-group-total">
                      <span>Subtotal ({gqty} pcs)</span>
                      <span>
                        {pricing?.calculating ? (
                          <span className="eov-calc">calc...</span>
                        ) : pricing?.fbp > 0 ? (
                          INR.format(pricing.fbp + dsSubtotal)
                        ) : (
                          INR.format(group.variants.reduce((s, v) => s + v.unitPrice * v.quantity, 0))
                        )}
                      </span>
                    </div>
                  </div>
                );
              })
            )}

            {/* Grand total summary */}
            {!loadingProducts && (
              <div className="edit-order-price-summary">
                <div className="edit-order-price-row">
                  <span>Product Total</span>
                  <span>{INR.format(totals.productTotal)}</span>
                </div>
                <div className="edit-order-price-divider" />
                <div className="edit-order-price-row">
                  <span>Shipping (LMD)</span>
                  <span>{INR.format(totals.baseLMD)}</span>
                </div>
                <div className="edit-order-price-row sub-row">
                  <span>↳ GST on Shipping ({gstShipLabel}%)</span>
                  <span>{INR.format(totals.gstOnShipping)}</span>
                </div>
                <div className="edit-order-price-row">
                  <span>Total Shipping</span>
                  <span>{INR.format(totals.shipping)}</span>
                </div>
                {totals.imDiscount > 0 && (
                  <>
                    <div className="edit-order-price-divider" />
                    <div className="edit-order-price-row">
                      <span style={{ color: "#4f46e5" }}>IM Points Discount</span>
                      <span style={{ color: "#4f46e5" }}>-{INR.format(totals.imDiscount)}</span>
                    </div>
                  </>
                )}
                <div className="edit-order-price-divider" />
                <div className="edit-order-price-row grand-total">
                  <span>Grand Total</span>
                  <span>{INR.format(totals.grand)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {zoomSrc && (
          <div
            className="edit-order-zoom-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Image preview"
            onClick={() => {
              zoomUrlsRef.current = [];
              setZoomSrc(null);
            }}
          >
            <button
              type="button"
              className="edit-order-zoom-close"
              onClick={() => {
                zoomUrlsRef.current = [];
                setZoomSrc(null);
              }}
              aria-label="Close"
            >
              ×
            </button>
            <img
              src={zoomSrc}
              alt=""
              className="edit-order-zoom-img"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                setZoomSrc((prev) => {
                  const list = zoomUrlsRef.current;
                  const i = list.indexOf(prev);
                  if (i >= 0 && i < list.length - 1) return list[i + 1];
                  e.target.onerror = null;
                  return prev;
                });
              }}
            />
          </div>
        )}

        <div className="edit-order-footer">
          <button
            className="edit-order-btn-calculate"
            onClick={handleCalculateAll}
            disabled={saving || loadingProducts || !pricingReady || calculatingAll || !pricingDirty}
            type="button"
          >
            <i className="fas fa-calculator" style={{ marginRight: 4 }} />
            {calculatingAll ? "Calculating..." : "Calculate"}
          </button>
          <button className="edit-order-btn-cancel" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="edit-order-btn-save"
            onClick={handleSave}
            disabled={saving || loadingProducts || !pricingReady || calculatingAll || pricingDirty}
            type="button"
            title={pricingDirty ? "Please click Calculate first to update prices & shipping" : ""}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
